import { type ReactNode } from 'react'
import { ChevronRight } from 'lucide-react'
import { Cell, type CellProps } from '@highlight/ui'
import { twMerge } from 'tailwind-merge'
import { SIDEPANEL_CELL_SURFACE, SIDEPANEL_CELL_TITLE } from './surface'

interface SidepanelCellRowProps {
  icon: ReactNode
  title: ReactNode
  subtitle?: ReactNode
  /** `center` lines a single-line title up with a trailing button. */
  align?: 'start' | 'center'
  trailing?: ReactNode
  /** The hover chevron after `trailing`. Off where the row's end has to line up with rows that draw none. */
  showChevron?: boolean
  titleClassName?: string
  className?: string
  focused?: boolean
  clickable?: boolean
  tabIndex?: number
  ariaLabel?: string
  onClick?: CellProps['onClick']
  onKeyDown?: CellProps['onKeyDown']
  onFocus?: CellProps['onFocus']
  onBlur?: CellProps['onBlur']
}

export function SidepanelCellRow({
  icon,
  title,
  subtitle,
  align = 'start',
  trailing,
  showChevron = true,
  titleClassName,
  className,
  focused,
  clickable,
  tabIndex,
  ariaLabel,
  onClick,
  onKeyDown,
  onFocus,
  onBlur,
}: SidepanelCellRowProps) {
  return (
    <Cell
      className={twMerge(SIDEPANEL_CELL_SURFACE, className)}
      focused={focused}
      clickable={clickable}
      tabIndex={tabIndex}
      aria-label={ariaLabel}
      onClick={onClick}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      <Cell.Header
        showChevron={false}
        // `min-w-0`, or a one-line title sizes the header to its whole length and out of the row.
        className={twMerge(
          'min-w-0',
          align === 'center'
            ? 'items-center [&_[data-slot=cell-header-content]]:items-center'
            : 'items-start [&_[data-slot=cell-header-content]]:items-start',
        )}
      >
        {icon}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Cell.Title className={twMerge(SIDEPANEL_CELL_TITLE, titleClassName)}>{title}</Cell.Title>
          {subtitle}
        </div>
        {trailing}
        {showChevron && (
          <ChevronRight className="text-foreground-secondary size-4 shrink-0 opacity-0 group-hover/cell:opacity-100 group-data-[popup-open]/cell:opacity-100" />
        )}
      </Cell.Header>
    </Cell>
  )
}
